import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {isCSR} from '@jobseeker/lotus/utils';
import {gladiatorService} from '@jobseeker/common-core/services';

import {HOME, UNSUBSCRIBE} from '@constants';
import {Chat, InjectSearchOverlay} from '@components';
import {NavLayout, SeoLayout, ResponsiveLayout} from '@layouts';
import {getConfigByApp, utilServerSideDeviceDetection} from '@utils';
import {loadSEO} from '@helpers';
import {metaInfo} from '@configs';
import UnsubscribeEmail from '@jobseeker/feature-profile/components/UnsubscribeEmail';
import {SeoIntrup} from '@layouts/SeoLayout';
import cookie from 'cookie';

import type {GetServerSidePropsContext} from 'next/types';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';

import dynamic from 'next/dynamic';
import type {AppState} from '@jobseeker/common-core/types';
import {openUrl} from '@jobseeker/common-core/utils/common';

type UnsubscribePageProps = {
  metaData: object;
  seoConfig: object;
  isBroken: boolean;
  isMobile: boolean;
};

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const Unsubscribe = ({
  metaData,
  seoConfig,
  isBroken,
  isMobile,
}: UnsubscribePageProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  const {searchOverlay, enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const onLoginSuccess = () => {
    if (isCSR()) {
      openUrl('/jobfeed', '_self');
    }
  };

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Unsubscribe Email',
      item: '',
    },
  ];

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={isAppIimjobs ? breadcrumbElements : []}
          >
            <InjectSearchOverlay isSearchEnabled={searchOverlay} />
            {isCSR() && (
              <SeoIntrup pageNo={0} nextPage={false} type={UNSUBSCRIBE} />
            )}
            <UnsubscribeEmail isBroken={isBroken} isMobile={isMobile} />
            {!isAppIimjobs && (
              <HiristAuthentication
                variant={VersionVariants.VERSION_TWO}
                onSuccess={onLoginSuccess}
                openDialog={enableLoginPopup}
                setOpenDialog={() => {
                  dispatch({
                    type: 'AppContext/set_login_popup',
                    payload: false,
                  });
                }}
              />
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const metaData = getConfigByApp(metaInfo, HOME, true, UNSUBSCRIBE);

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const seoConfig = await loadSEO(HOME);

    let isBroken = false;
    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);
        console.log(
          '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
          context.req?.headers
        );

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });
          isBroken = true;
        }
      }
    } catch (error) {
      console.log('🚀 ~ store=> ~ error:', error);
    }

    return {
      props: {
        metaData,
        seoConfig,
        isBroken,
        isMobile,
      },
    };
  }
);

export default Unsubscribe;
